import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountInfoState } from '../../../account-info-header/models/AccountInfoState';
import { selectInfo } from '../../../account-info-header/reducers';
import { selectStagedRequestingAccountHolderId , selectNickname } from '../../../create-request/reducers';
import { selectIsTransactionsViewPage, selectIsStandingAuthViewPage, selectViewDetails } from '../../../transaction-view/reducers';
import { accountDetailsLabel, pageInContext } from '../../utilities/Constants';
import { selectConfirmationPageTaxId, selectViewAccountDetails, selectViewInContext, selectViewPageTaxId } from '../../reducers';
import { selectIsFromTableOrView } from '../../../standing-authorizations/reducers';
import {
  selectStagedTransactionForm
} from 'projects/money-movement-ui/src/app/create-request/reducers';
import {
  WireTransactionDetails,
  isWireForm
} from 'projects/money-movement-ui/src/app/create-request/models/form/TransactionForm';
import { ViewAccountDetails } from '../../../create-request/models/view-page/ViewPage';
import { hasContent } from '../../utilities/string-utils';


@Component({
  standalone: false,
  selector: 'mm-account-details-section',
  templateUrl: './account-details-section.component.html',
  styleUrls: ['./account-details-section.component.scss']
})
export class AccountDetailsSectionComponent implements OnInit {
  accountInfo: AccountInfoState;
  requestingAccountHolderId: string;
  requestingAccountHolderFullName: string;
  nickname: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  readonly labels = accountDetailsLabel;
  isStandingAuthViewPage: boolean;
  isTransactionViewPage: boolean;
  taxId: string;
  selectedAuthorization: string;
  primaryAccountHolder: string[] = [];
  isViewTemplate: boolean = false;
  currentStep: string;
  isFromTableOrView: boolean;
  useFiveColumns: boolean = false;
  useSloa: boolean = false;
  viewAccountDetails: ViewAccountDetails | undefined;


  constructor(private store: Store) { }

  ngOnInit(): void {
    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe(accountInfo => {
      this.accountInfo = accountInfo;
    });

    this.store.select(selectViewAccountDetails).pipe(takeUntil(this.destroy$)).subscribe(viewAccountDetails => {
      this.viewAccountDetails = viewAccountDetails;
    });

    this.primaryAccountHolder.push(this.accountInfo.subAccountNameLine1)
    if (hasContent(this.accountInfo.subAccountNameLine2)) {
      this.primaryAccountHolder.push(this.accountInfo.subAccountNameLine2)
    }
    if (hasContent(this.accountInfo.subAccountNameLine3)) {
      this.primaryAccountHolder.push(this.accountInfo.subAccountNameLine3)
    }

    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe(step => this.currentStep = step)
    this.store.select(selectIsStandingAuthViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isStandingAuthViewPage = value);
    this.store.select(selectIsTransactionsViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isTransactionViewPage = value);
    this.store.select(selectIsFromTableOrView).pipe(takeUntil(this.destroy$)).subscribe(value => this.isFromTableOrView = value);
    this.store.select(selectStagedTransactionForm).pipe(takeUntil(this.destroy$)).subscribe(form => {
      // if all three properties Account number, BIC, and clearing sys ID are present then the width has to change as there will be 5 columns now instead of 4
      // so for changing the width on UI we need the below conditions
      if (isWireForm(form) &&
          ((form.transactionDetails?.intermediaryAccountNumber && form.transactionDetails?.intermediaryBIC && form.transactionDetails?.intermediaryClearingSysId) ||
          (form.transactionDetails?.secondIntermediaryAccountNumber && form.transactionDetails?.secondIntermediaryBIC && form.transactionDetails?.secondIntermediaryClearingSysId) ||
          (form.transactionDetails?.thirdIntermediaryAccountNumber && form.transactionDetails?.thirdIntermediaryBIC && form.transactionDetails?.thirdIntermediaryClearingSysId))) {
        this.useFiveColumns = true;
        this.useSloa = form.extraPrompts.useStandingAuth.toLowerCase() === 'yes' || form.extraPrompts.useBundledStandingAuth === 'yes';
      }
    });
    if (this.isStandingAuthViewPage || this.isTransactionViewPage) {
      this.store.select(selectViewPageTaxId).pipe(takeUntil(this.destroy$)).subscribe(value => this.taxId = value);
    } else {
      this.store.select(selectConfirmationPageTaxId).pipe(takeUntil(this.destroy$)).subscribe(value => this.taxId = value);
    }
    this.store.select(selectStagedRequestingAccountHolderId).pipe(takeUntil(this.destroy$)).subscribe(requestingAccountHolderId => {
      this.requestingAccountHolderId = requestingAccountHolderId ? requestingAccountHolderId : '';
      this.setRequestingAccountHolderName();
    })
    this.store.select(selectViewDetails).pipe(takeUntil(this.destroy$)).subscribe(viewDetails => {
      if (viewDetails.type === 'template') {
        this.isViewTemplate = true;
      }
    })


    this.store.select(selectNickname).pipe(takeUntil(this.destroy$)).subscribe(selectedNickname => {
      this.nickname = selectedNickname;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  setRequestingAccountHolderName(){
    if (this.requestingAccountHolderId) {
      for (let i = 0; i < this.accountInfo.eauthorization.customers.length; i++) {
        if (this.accountInfo.eauthorization.customers[i].customerId === this.requestingAccountHolderId) {
          this.requestingAccountHolderFullName = `${this.accountInfo.eauthorization.customers[i].firstName} ${this.accountInfo.eauthorization.customers[i].lastName}`
        }
      }
    }
  }

  get displayAccountHolderId(): boolean {
    return !!this.requestingAccountHolderId && !this.isViewTemplate && !(this.requestingAccountHolderFullName?.trim() === "");
  }

  get isReviewPage(): boolean {
    return this.currentStep === pageInContext.review
  }

  get regType(): string {
    if (hasContent(this.accountInfo.registrationType)) {
      return this.accountInfo.registrationType;
    } else {
      return this.viewAccountDetails?.regType ?? '';
    }
  }

  get accountNumber(): string {
    // this is the latest accountId after a successful inquiry call so use this specifically for msv
    // since accountId within accountInfo can be stale when viewing for msv
    if (this.currentStep === pageInContext.viewMsv) {
      return this.viewAccountDetails?.subAccountId ?? '';
    }
    if (this.accountInfo.accountId) {
      return this.accountInfo.accountId;
    } else {
      return this.viewAccountDetails?.subAccountId ?? '';
    }
  }

  get accountHolder(): string[] {
    let accountHolder: string[] = [];

    if (hasContent(this.accountInfo.subAccountNameLine1)) {
      accountHolder.push(this.accountInfo.subAccountNameLine1);
      if (hasContent(this.accountInfo.subAccountNameLine2)) {
        accountHolder.push(this.accountInfo.subAccountNameLine2);
      }
      if (hasContent(this.accountInfo.subAccountNameLine3)) {
        accountHolder.push(this.accountInfo.subAccountNameLine3);
      }
    } else {
      accountHolder.push(this.viewAccountDetails?.accountHolderLine1 ?? '');
      if (hasContent(this.viewAccountDetails?.accountHolderLine2)) {
        accountHolder.push(this.viewAccountDetails!.accountHolderLine2!);
      }
      if (hasContent(this.viewAccountDetails?.accountHolderLine3)) {
        accountHolder.push(this.viewAccountDetails!.accountHolderLine3!);
      }
    }

    return accountHolder;
  }

  get masterAccount(): string {
    if (this.accountInfo?.userContext === 'PALACCOUNT') {
      return this.accountInfo?.primaryMaster || '';
    }
    return this.accountInfo?.masterAccount || '';
  }
}
